/**
 * IC Projects screen — primary landing for office/QA/billing users.
 */
import { api } from "../api/client.ts";
import type { IcProject } from "../types.ts";

export interface IcProjectsPageOptions {
  container: HTMLElement;
  onOpen?: (project: IcProject) => void;
  toast?: (msg: string, kind?: "info" | "error") => void;
}

const STATUS_COLOR: Record<string, string> = {
  draft: "#888",
  active: "#4a9eff",
  closed: "#4aff6a",
  cancelled: "#ff4a4a",
};

const STATUS_LABELS = ["all", "draft", "active", "closed", "cancelled"];

const DATE_PRESETS: Array<{ key: string; label: string }> = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "", label: "All" },
];

type SortKey = "updated" | "loss_date" | "last_inspection" | "status" | "client_id";
type SortDir = "asc" | "desc";

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: "updated", label: "Updated" },
  { key: "loss_date", label: "Loss date" },
  { key: "last_inspection", label: "Last inspection" },
  { key: "status", label: "Status" },
  { key: "client_id", label: "Client project ID" },
];

const URL_PARAM = "icp"; // single key holds JSON-encoded state to keep query clean

const SELECT_STYLE = `
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  font-size: 12px;
  padding: 5px 8px;
  cursor: pointer;
`;

const DATE_INPUT_STYLE = `
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  font-size: 11px;
  padding: 3px 6px;
  cursor: pointer;
`;

const TOOLBAR_BTN_STYLE = `
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 6px; color: var(--text); font-size: 12px;
  padding: 6px 10px; cursor: pointer; transition: border-color 120ms, background 120ms;
  white-space: nowrap;
`;

export class IcProjectsPage {
  private root: HTMLElement;
  private grid: HTMLElement;
  private status: HTMLElement;
  private opts: IcProjectsPageOptions;
  private projects: IcProject[] = [];

  // Filter state
  private filterQuery = "";
  private filterStatus = "all";
  private filterLossType = "";
  private filterInspector = "";
  private filterLossFrom = "";
  private filterLossTo = "";
  private filterInspFrom = "";
  private filterInspTo = "";

  // Sort state
  private sortKey: SortKey = "updated";
  private sortDir: SortDir = "desc";

  // Filter UI refs
  private searchInput!: HTMLInputElement;
  private lossTypeSelect!: HTMLSelectElement;
  private inspectorSelect!: HTMLSelectElement;
  private statusBtns = new Map<string, HTMLElement>();
  private lossPresetBtns = new Map<string, HTMLElement>();
  private inspPresetBtns = new Map<string, HTMLElement>();
  private lossFromInput!: HTMLInputElement;
  private lossToInput!: HTMLInputElement;
  private inspFromInput!: HTMLInputElement;
  private inspToInput!: HTMLInputElement;
  private filtersBtn!: HTMLButtonElement;
  private filtersCount!: HTMLElement;
  private drawer!: HTMLElement;
  private drawerOpen = false;
  private sortBtn!: HTMLButtonElement;
  private sortMenu!: HTMLElement;
  private sortMenuOpen = false;
  private resultsCount!: HTMLElement;

  private searchDebounce: number | null = null;
  private docClickHandler: ((e: MouseEvent) => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(opts: IcProjectsPageOptions) {
    this.opts = opts;
    this.root = opts.container;
    this.root.innerHTML = "";
    this.root.style.cssText = `
      flex-direction: column;
      padding: 32px;
      overflow-y: auto;
      gap: 20px;
    `;

    this.restoreFromUrl();

    // Header
    const header = document.createElement("div");
    header.style.cssText = "display:flex;align-items:center;gap:16px;";
    const h1 = document.createElement("h1");
    h1.textContent = "Projects";
    h1.style.cssText = "font-size:22px;font-weight:600;margin-right:auto";
    this.status = document.createElement("span");
    this.status.style.cssText = "font-size:12px;color:var(--text-muted)";
    header.appendChild(h1);
    header.appendChild(this.status);
    this.root.appendChild(header);

    // Toolbar + drawer
    const filterShell = document.createElement("div");
    filterShell.style.cssText = "display:flex;flex-direction:column;gap:0;";
    filterShell.appendChild(this.buildToolbar());
    filterShell.appendChild(this.buildDrawer());
    this.root.appendChild(filterShell);

    // Grid
    this.grid = document.createElement("div");
    this.grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 16px;
    `;
    this.root.appendChild(this.grid);

    // Click-outside / Esc to close drawer + sort menu
    this.docClickHandler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (this.drawerOpen && !this.drawer.contains(t) && !this.filtersBtn.contains(t)) {
        this.toggleDrawer(false);
      }
      if (this.sortMenuOpen && !this.sortMenu.contains(t) && !this.sortBtn.contains(t)) {
        this.toggleSortMenu(false);
      }
    };
    document.addEventListener("mousedown", this.docClickHandler);
    this.keyHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (this.sortMenuOpen) this.toggleSortMenu(false);
      else if (this.drawerOpen) this.toggleDrawer(false);
    };
    document.addEventListener("keydown", this.keyHandler);
  }

  // ── Toolbar ──────────────────────────────────────────────────────────────

  private buildToolbar(): HTMLElement {
    const bar = document.createElement("div");
    bar.style.cssText = `
      display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
      padding: 10px 14px;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
    `;

    // Search
    const searchWrap = document.createElement("div");
    searchWrap.style.cssText = `
      position: relative; flex: 1 1 220px; min-width: 200px; max-width: 380px;
    `;
    const searchIcon = document.createElement("span");
    searchIcon.textContent = "🔍";
    searchIcon.style.cssText = `
      position:absolute;left:8px;top:50%;transform:translateY(-50%);
      font-size:11px;opacity:.6;pointer-events:none;
    `;
    this.searchInput = document.createElement("input");
    this.searchInput.type = "search";
    this.searchInput.placeholder = "Search project ID, address, inspector…";
    this.searchInput.value = this.filterQuery;
    this.searchInput.style.cssText = `
      width: 100%; box-sizing: border-box;
      background: var(--bg); border: 1px solid var(--border);
      border-radius: 6px; color: var(--text); font-size: 12px;
      padding: 6px 10px 6px 28px;
    `;
    this.searchInput.oninput = () => {
      if (this.searchDebounce !== null) window.clearTimeout(this.searchDebounce);
      this.searchDebounce = window.setTimeout(() => {
        this.filterQuery = this.searchInput.value.trim();
        this.syncUrl();
        this.applyFilters();
      }, 150);
    };
    searchWrap.appendChild(searchIcon);
    searchWrap.appendChild(this.searchInput);
    bar.appendChild(searchWrap);

    // Status chips
    const chips = document.createElement("div");
    chips.style.cssText = "display:flex;align-items:center;flex-wrap:wrap;gap:6px;";
    for (const s of STATUS_LABELS) {
      const btn = document.createElement("button");
      btn.textContent = s === "all" ? "All" : capitalize(s);
      btn.style.cssText = `
        padding: 4px 10px; border-radius: 99px; border: 1px solid;
        font-size: 11px; font-weight: 600; cursor: pointer;
        text-transform: uppercase; letter-spacing: .4px; transition: all 120ms;
      `;
      this.statusBtns.set(s, btn);
      btn.onclick = () => this.setStatusFilter(s);
      chips.appendChild(btn);
    }
    bar.appendChild(chips);
    this.refreshStatusBtnStyles();

    // Spacer
    const spacer = document.createElement("div");
    spacer.style.cssText = "flex:1;min-width:8px;";
    bar.appendChild(spacer);

    // Results count
    this.resultsCount = document.createElement("span");
    this.resultsCount.style.cssText = "font-size:11px;color:var(--text-muted);";
    bar.appendChild(this.resultsCount);

    // Sort button + menu
    const sortWrap = document.createElement("div");
    sortWrap.style.cssText = "position:relative;";
    this.sortBtn = document.createElement("button");
    this.sortBtn.style.cssText = TOOLBAR_BTN_STYLE;
    this.refreshSortBtnLabel();
    this.sortBtn.onclick = (e) => {
      e.stopPropagation();
      this.toggleSortMenu(!this.sortMenuOpen);
    };
    this.sortMenu = this.buildSortMenu();
    sortWrap.appendChild(this.sortBtn);
    sortWrap.appendChild(this.sortMenu);
    bar.appendChild(sortWrap);

    // Filters drawer toggle
    this.filtersBtn = document.createElement("button");
    this.filtersBtn.style.cssText = TOOLBAR_BTN_STYLE;
    const gear = document.createElement("span");
    gear.textContent = "⚙";
    const flbl = document.createElement("span");
    flbl.textContent = "Filters";
    this.filtersCount = document.createElement("span");
    this.filtersCount.style.cssText = `
      display:none; padding: 0 6px; min-width: 16px; height: 16px;
      border-radius: 99px; background: var(--accent); color: #fff;
      font-size: 10px; font-weight: 700; line-height: 16px; text-align: center;
    `;
    const caret = document.createElement("span");
    caret.textContent = "▾";
    caret.style.cssText = "font-size:9px;opacity:.7;";
    this.filtersBtn.appendChild(gear);
    this.filtersBtn.appendChild(flbl);
    this.filtersBtn.appendChild(this.filtersCount);
    this.filtersBtn.appendChild(caret);
    this.filtersBtn.onclick = (e) => {
      e.stopPropagation();
      this.toggleDrawer(!this.drawerOpen);
    };
    bar.appendChild(this.filtersBtn);

    // Refresh
    const refresh = document.createElement("button");
    refresh.style.cssText = TOOLBAR_BTN_STYLE;
    refresh.textContent = "↻ Refresh";
    refresh.onclick = () => this.refresh();
    bar.appendChild(refresh);

    return bar;
  }

  private buildSortMenu(): HTMLElement {
    const menu = document.createElement("div");
    menu.style.cssText = `
      position: absolute; top: calc(100% + 4px); right: 0; z-index: 20;
      background: var(--surface); border: 1px solid var(--border);
      border-radius: 8px; padding: 4px; min-width: 200px;
      box-shadow: 0 6px 24px rgba(0,0,0,.3); display: none;
    `;
    for (const opt of SORT_OPTIONS) {
      const item = document.createElement("button");
      item.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        width: 100%; padding: 7px 10px; border: 0; background: transparent;
        color: var(--text); font-size: 12px; cursor: pointer; border-radius: 4px;
        text-align: left;
      `;
      const label = document.createElement("span");
      label.textContent = opt.label;
      const dirEl = document.createElement("span");
      dirEl.style.cssText = "font-size:11px;color:var(--text-muted);";
      item.appendChild(label);
      item.appendChild(dirEl);
      item.onmouseenter = () => (item.style.background = "var(--border)");
      item.onmouseleave = () => (item.style.background = "transparent");
      item.onclick = () => {
        if (this.sortKey === opt.key) {
          this.sortDir = this.sortDir === "desc" ? "asc" : "desc";
        } else {
          this.sortKey = opt.key;
          this.sortDir = "desc";
        }
        this.refreshSortBtnLabel();
        this.refreshSortMenuMarks();
        this.syncUrl();
        this.applyFilters();
      };
      menu.appendChild(item);
      (item as unknown as HTMLElement & { _opt: typeof opt })._opt = opt;
    }
    return menu;
  }

  private refreshSortMenuMarks(): void {
    for (const child of Array.from(this.sortMenu.children)) {
      const item = child as HTMLElement & { _opt?: { key: SortKey } };
      const dirEl = item.children[1] as HTMLElement;
      if (item._opt && item._opt.key === this.sortKey) {
        dirEl.textContent = this.sortDir === "desc" ? "↓" : "↑";
        dirEl.style.color = "var(--accent)";
      } else {
        dirEl.textContent = "";
      }
    }
  }

  private refreshSortBtnLabel(): void {
    const cur = SORT_OPTIONS.find((o) => o.key === this.sortKey)!;
    const arrow = this.sortDir === "desc" ? "↓" : "↑";
    this.sortBtn.innerHTML = "";
    const s1 = document.createElement("span");
    s1.textContent = "⇅";
    s1.style.opacity = ".7";
    const s2 = document.createElement("span");
    s2.textContent = cur.label;
    const s3 = document.createElement("span");
    s3.textContent = arrow;
    s3.style.cssText = "color:var(--accent);font-weight:700;";
    const s4 = document.createElement("span");
    s4.textContent = "▾";
    s4.style.cssText = "font-size:9px;opacity:.7;";
    this.sortBtn.appendChild(s1);
    this.sortBtn.appendChild(s2);
    this.sortBtn.appendChild(s3);
    this.sortBtn.appendChild(s4);
  }

  private toggleSortMenu(open: boolean): void {
    this.sortMenuOpen = open;
    this.sortMenu.style.display = open ? "block" : "none";
    if (open) this.refreshSortMenuMarks();
  }

  // ── Drawer ───────────────────────────────────────────────────────────────

  private buildDrawer(): HTMLElement {
    const drawer = document.createElement("div");
    drawer.style.cssText = `
      overflow: hidden; max-height: 0; transition: max-height 180ms ease;
      background: var(--surface); border: 1px solid var(--border); border-top: 0;
      border-radius: 0 0 var(--radius) var(--radius);
      margin-top: -1px;
    `;
    this.drawer = drawer;

    const inner = document.createElement("div");
    inner.style.cssText = "padding: 14px 16px; display:flex; flex-direction:column; gap:12px;";
    drawer.appendChild(inner);

    // Row: selects
    const selectsRow = document.createElement("div");
    selectsRow.style.cssText = "display:flex;flex-wrap:wrap;gap:16px;";

    const lossWrap = labeledField("Loss type");
    this.lossTypeSelect = document.createElement("select");
    this.lossTypeSelect.style.cssText = SELECT_STYLE;
    this.lossTypeSelect.innerHTML = '<option value="">All loss types</option>';
    this.lossTypeSelect.onchange = () => {
      this.filterLossType = this.lossTypeSelect.value;
      this.refreshFiltersCount();
      this.syncUrl();
      this.applyFilters();
    };
    lossWrap.appendChild(this.lossTypeSelect);
    selectsRow.appendChild(lossWrap);

    const inspWrap = labeledField("Inspector");
    this.inspectorSelect = document.createElement("select");
    this.inspectorSelect.style.cssText = SELECT_STYLE;
    this.inspectorSelect.innerHTML = '<option value="">All inspectors</option>';
    this.inspectorSelect.onchange = () => {
      this.filterInspector = this.inspectorSelect.value;
      this.refreshFiltersCount();
      this.syncUrl();
      this.applyFilters();
    };
    inspWrap.appendChild(this.inspectorSelect);
    selectsRow.appendChild(inspWrap);

    inner.appendChild(selectsRow);

    // Loss date row
    const { row: row2, fromInput: lossFrom, toInput: lossTo } = this.buildDateRow(
      "Loss date",
      this.lossPresetBtns,
      (p) => this.setLossPreset(p)
    );
    this.lossFromInput = lossFrom;
    this.lossToInput = lossTo;
    lossFrom.value = this.filterLossFrom;
    lossTo.value = this.filterLossTo;
    lossFrom.onchange = () => this.onManualLossDate();
    lossTo.onchange = () => this.onManualLossDate();
    inner.appendChild(row2);

    // Insp date row
    const { row: row3, fromInput: inspFrom, toInput: inspTo } = this.buildDateRow(
      "Last inspection",
      this.inspPresetBtns,
      (p) => this.setInspPreset(p)
    );
    this.inspFromInput = inspFrom;
    this.inspToInput = inspTo;
    inspFrom.value = this.filterInspFrom;
    inspTo.value = this.filterInspTo;
    inspFrom.onchange = () => this.onManualInspDate();
    inspTo.onchange = () => this.onManualInspDate();
    inner.appendChild(row3);

    // Footer
    const footer = document.createElement("div");
    footer.style.cssText = "display:flex;justify-content:flex-end;gap:8px;padding-top:4px;";
    const reset = document.createElement("button");
    reset.textContent = "Reset filters";
    reset.style.cssText = TOOLBAR_BTN_STYLE;
    reset.onclick = () => this.resetAll();
    const done = document.createElement("button");
    done.textContent = "Done";
    done.style.cssText = `
      ${TOOLBAR_BTN_STYLE}
      background: var(--accent); border-color: var(--accent); color: #fff;
    `;
    done.onclick = () => this.toggleDrawer(false);
    footer.appendChild(reset);
    footer.appendChild(done);
    inner.appendChild(footer);

    this.refreshLossPresetActive();
    this.refreshInspPresetActive();

    return drawer;
  }

  private buildDateRow(
    label: string,
    presetBtns: Map<string, HTMLElement>,
    onPreset: (key: string) => void
  ): { row: HTMLElement; fromInput: HTMLInputElement; toInput: HTMLInputElement } {
    const wrap = labeledField(label);
    wrap.style.flexDirection = "column";

    const row = document.createElement("div");
    row.style.cssText = "display:flex;align-items:center;flex-wrap:wrap;gap:8px;";

    for (const { key, label: pLabel } of DATE_PRESETS) {
      const btn = document.createElement("button");
      btn.textContent = pLabel;
      btn.style.cssText = presetBtnStyle(false);
      presetBtns.set(key, btn);
      btn.onclick = () => onPreset(key);
      row.appendChild(btn);
    }

    const orLabel = document.createElement("span");
    orLabel.textContent = "or";
    orLabel.style.cssText = "font-size:11px;color:var(--text-muted);margin:0 2px;";
    row.appendChild(orLabel);

    const fromInput = document.createElement("input");
    fromInput.type = "date";
    fromInput.style.cssText = DATE_INPUT_STYLE;

    const dash = document.createElement("span");
    dash.textContent = "—";
    dash.style.cssText = "font-size:11px;color:var(--text-muted);";

    const toInput = document.createElement("input");
    toInput.type = "date";
    toInput.style.cssText = DATE_INPUT_STYLE;

    row.appendChild(fromInput);
    row.appendChild(dash);
    row.appendChild(toInput);

    wrap.appendChild(row);
    return { row: wrap, fromInput, toInput };
  }

  private toggleDrawer(open: boolean): void {
    this.drawerOpen = open;
    if (open) {
      this.drawer.style.maxHeight = this.drawer.scrollHeight + 40 + "px";
      this.filtersBtn.style.borderColor = "var(--accent)";
    } else {
      this.drawer.style.maxHeight = "0";
      this.filtersBtn.style.borderColor = "var(--border)";
    }
  }

  // ── Filter actions ───────────────────────────────────────────────────────

  private onManualLossDate(): void {
    this.filterLossFrom = this.lossFromInput.value;
    this.filterLossTo = this.lossToInput.value;
    this.refreshLossPresetActive();
    this.refreshFiltersCount();
    this.syncUrl();
    this.applyFilters();
  }

  private onManualInspDate(): void {
    this.filterInspFrom = this.inspFromInput.value;
    this.filterInspTo = this.inspToInput.value;
    this.refreshInspPresetActive();
    this.refreshFiltersCount();
    this.syncUrl();
    this.applyFilters();
  }

  private setStatusFilter(s: string): void {
    this.filterStatus = s;
    this.refreshStatusBtnStyles();
    this.syncUrl();
    this.applyFilters();
  }

  private refreshStatusBtnStyles(): void {
    for (const [key, btn] of this.statusBtns) {
      const active = key === this.filterStatus;
      const color = key === "all" ? "#888" : (STATUS_COLOR[key] || "#888");
      if (active) {
        btn.style.background = color;
        btn.style.borderColor = color;
        btn.style.color = key === "closed" ? "#000" : "#fff";
        btn.style.opacity = "1";
      } else {
        btn.style.background = "transparent";
        btn.style.borderColor = key === "all" ? "var(--border)" : (STATUS_COLOR[key] || "var(--border)");
        btn.style.color = key === "all" ? "var(--text-muted)" : (STATUS_COLOR[key] || "var(--text-muted)");
        btn.style.opacity = "0.6";
      }
    }
  }

  private setLossPreset(preset: string): void {
    const range = presetToDates(preset);
    this.filterLossFrom = range.from;
    this.filterLossTo = range.to;
    this.lossFromInput.value = range.from;
    this.lossToInput.value = range.to;
    this.refreshLossPresetActive();
    this.refreshFiltersCount();
    this.syncUrl();
    this.applyFilters();
  }

  private setInspPreset(preset: string): void {
    const range = presetToDates(preset);
    this.filterInspFrom = range.from;
    this.filterInspTo = range.to;
    this.inspFromInput.value = range.from;
    this.inspToInput.value = range.to;
    this.refreshInspPresetActive();
    this.refreshFiltersCount();
    this.syncUrl();
    this.applyFilters();
  }

  private refreshLossPresetActive(): void {
    const matched = matchPreset(this.filterLossFrom, this.filterLossTo);
    refreshPresetStyles(this.lossPresetBtns, matched);
  }

  private refreshInspPresetActive(): void {
    const matched = matchPreset(this.filterInspFrom, this.filterInspTo);
    refreshPresetStyles(this.inspPresetBtns, matched);
  }

  private activeFilterCount(): number {
    let n = 0;
    if (this.filterLossType) n++;
    if (this.filterInspector) n++;
    if (this.filterLossFrom || this.filterLossTo) n++;
    if (this.filterInspFrom || this.filterInspTo) n++;
    return n;
  }

  private refreshFiltersCount(): void {
    const n = this.activeFilterCount();
    if (n > 0) {
      this.filtersCount.textContent = String(n);
      this.filtersCount.style.display = "inline-block";
    } else {
      this.filtersCount.style.display = "none";
    }
    if (this.drawerOpen) {
      this.drawer.style.maxHeight = this.drawer.scrollHeight + 40 + "px";
    }
  }

  private resetAll(): void {
    this.filterQuery = "";
    this.filterStatus = "all";
    this.filterLossType = "";
    this.filterInspector = "";
    this.filterLossFrom = "";
    this.filterLossTo = "";
    this.filterInspFrom = "";
    this.filterInspTo = "";
    this.searchInput.value = "";
    this.lossTypeSelect.value = "";
    this.inspectorSelect.value = "";
    this.lossFromInput.value = "";
    this.lossToInput.value = "";
    this.inspFromInput.value = "";
    this.inspToInput.value = "";
    this.refreshStatusBtnStyles();
    this.refreshLossPresetActive();
    this.refreshInspPresetActive();
    this.refreshFiltersCount();
    this.syncUrl();
    this.applyFilters();
  }

  private rebuildDropdowns(): void {
    const lossTypes = [
      ...new Set(
        this.projects
          .map((p) => p.assignment.loss_type)
          .filter((x): x is string => !!x)
      ),
    ].sort();

    const inspectors = [
      ...new Set(this.projects.map((p) => p.assignment.inspector).filter(Boolean)),
    ].sort();

    this.lossTypeSelect.innerHTML = '<option value="">All loss types</option>';
    for (const lt of lossTypes) {
      const opt = document.createElement("option");
      opt.value = lt;
      opt.textContent = lt;
      if (lt === this.filterLossType) opt.selected = true;
      this.lossTypeSelect.appendChild(opt);
    }

    this.inspectorSelect.innerHTML = '<option value="">All inspectors</option>';
    for (const insp of inspectors) {
      const opt = document.createElement("option");
      opt.value = insp;
      opt.textContent = insp;
      if (insp === this.filterInspector) opt.selected = true;
      this.inspectorSelect.appendChild(opt);
    }
  }

  // ── Apply & render ───────────────────────────────────────────────────────

  private applyFilters(): void {
    let list = this.projects.slice();

    if (this.filterStatus !== "all") {
      list = list.filter((p) => p.assignment.status === this.filterStatus);
    }
    if (this.filterLossType) {
      list = list.filter((p) => p.assignment.loss_type === this.filterLossType);
    }
    if (this.filterInspector) {
      list = list.filter((p) => p.assignment.inspector === this.filterInspector);
    }
    if (this.filterLossFrom || this.filterLossTo) {
      list = list.filter((p) =>
        inDateRange(p.assignment.loss_date, this.filterLossFrom, this.filterLossTo)
      );
    }
    if (this.filterInspFrom || this.filterInspTo) {
      list = list.filter((p) =>
        inDateRange(
          p.summary.latest_inspection_date,
          this.filterInspFrom,
          this.filterInspTo
        )
      );
    }
    if (this.filterQuery) {
      const q = this.filterQuery.toLowerCase();
      list = list.filter((p) => projectMatchesQuery(p, q));
    }

    list = sortProjects(list, this.sortKey, this.sortDir);

    this.updateResultsCount(list.length);
    this.renderList(list);
  }

  private updateResultsCount(shown: number): void {
    const total = this.projects.length;
    if (shown === total) {
      this.resultsCount.textContent = `${total} project${total === 1 ? "" : "s"}`;
    } else {
      this.resultsCount.textContent = `${shown} of ${total}`;
    }
  }

  async refresh(): Promise<void> {
    this.setStatus("Loading…");
    this.grid.innerHTML = "";

    try {
      const health = await api.ic.health();
      if (!health.configured) {
        this.setStatus(
          "inspect_connect proxy is not configured. " +
            "Set IC_BASE_URL and IC_SERVICE_TOKEN in backend env."
        );
        return;
      }
    } catch (err) {
      this.setStatus(`Health check failed: ${(err as Error).message}`);
      return;
    }

    try {
      const resp = await api.ic.listProjects(0);
      this.projects = resp.projects;
      this.rebuildDropdowns();
      this.refreshFiltersCount();
      this.applyFilters();
      this.setStatus(`v${resp.max_state_version}`);
    } catch (err) {
      const msg = (err as Error).message || "Failed to load projects";
      this.setStatus(`Error: ${msg}`);
      this.opts.toast?.(`Failed to load IC projects: ${msg}`, "error");
    }
  }

  private setStatus(text: string): void {
    this.status.textContent = text;
  }

  private renderList(list: IcProject[]): void {
    this.grid.innerHTML = "";
    if (list.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "display:flex;flex-direction:column;align-items:flex-start;gap:8px;";
      const msg = document.createElement("p");
      msg.style.cssText = "color:var(--text-muted);margin:0;";
      const hasFilters =
        this.filterQuery ||
        this.filterStatus !== "all" ||
        this.activeFilterCount() > 0;
      msg.textContent =
        this.projects.length === 0
          ? "No projects in inspect_connect yet. Mobile inspectors will populate this list."
          : "No projects match the current filters.";
      empty.appendChild(msg);
      if (this.projects.length > 0 && hasFilters) {
        const clr = document.createElement("button");
        clr.textContent = "Clear filters";
        clr.style.cssText = TOOLBAR_BTN_STYLE;
        clr.onclick = () => this.resetAll();
        empty.appendChild(clr);
      }
      this.grid.appendChild(empty);
      return;
    }
    for (const proj of list) {
      this.grid.appendChild(this.makeCard(proj));
    }
  }

  private makeCard(proj: IcProject): HTMLElement {
    const card = document.createElement("div");
    card.className = "project-card clickable";
    card.style.cssText = `
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 16px;
      cursor: pointer;
      transition: border-color 120ms;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;
    card.onmouseenter = () => (card.style.borderColor = "var(--accent)");
    card.onmouseleave = () => (card.style.borderColor = "var(--border)");
    card.onclick = () => this.opts.onOpen?.(proj);

    // Title row: client project id + status badge
    const titleRow = document.createElement("div");
    titleRow.style.cssText =
      "display:flex;align-items:baseline;gap:8px;justify-content:space-between";
    const title = document.createElement("div");
    title.style.cssText = "font-weight:600;font-size:14px;word-break:break-all";
    title.textContent =
      proj.assignment.client_project_id ||
      `Project ${proj.project_id.slice(0, 8)}`;
    const statusBadge = document.createElement("span");
    statusBadge.textContent = proj.assignment.status;
    statusBadge.style.cssText = `
      display:inline-block;padding:2px 8px;border-radius:99px;
      font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;
      background:${STATUS_COLOR[proj.assignment.status] || "#666"};color:#fff;flex-shrink:0;
    `;
    titleRow.appendChild(title);
    titleRow.appendChild(statusBadge);
    card.appendChild(titleRow);

    // Address
    const addr = formatAddress(proj.assignment.address);
    if (addr) {
      const addrEl = document.createElement("div");
      addrEl.style.cssText = "font-size:12px;color:var(--text-muted)";
      addrEl.textContent = addr;
      card.appendChild(addrEl);
    }

    // Inspector + loss type
    const meta = document.createElement("div");
    meta.style.cssText = "font-size:11px;color:var(--text-muted)";
    const inspector = proj.assignment.inspector || "—";
    const loss = proj.assignment.loss_type || "—";
    meta.textContent = `${inspector} · ${loss}`;
    card.appendChild(meta);

    // Indicator chips
    const chips = document.createElement("div");
    chips.style.cssText = "display:flex;flex-wrap:wrap;gap:6px;margin-top:4px";
    const s = proj.summary;

    const inspCount = s.inspection_count;
    chips.appendChild(
      makeChip(
        `${inspCount} inspection${inspCount === 1 ? "" : "s"}`,
        inspCount > 0 ? "ok" : "muted"
      )
    );
    if (s.equipment_active > 0) {
      chips.appendChild(makeChip(`${s.equipment_active} equip`, "info"));
    }
    if (s.has_roomplan) chips.appendChild(makeChip("RoomPlan", "info"));
    if (s.has_pano) chips.appendChild(makeChip("Pano", "info"));
    card.appendChild(chips);

    // Footer: updated_at relative
    const footer = document.createElement("div");
    footer.style.cssText =
      "font-size:11px;color:var(--text-muted);margin-top:auto;padding-top:6px;";
    footer.textContent = `Updated ${formatRelative(proj.updated_at)}`;
    card.appendChild(footer);

    return card;
  }

  // ── URL persistence ──────────────────────────────────────────────────────

  private restoreFromUrl(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get(URL_PARAM);
      if (!raw) return;
      const s = JSON.parse(raw) as Partial<{
        q: string;
        status: string;
        loss: string;
        inspector: string;
        lossFrom: string;
        lossTo: string;
        inspFrom: string;
        inspTo: string;
        sort: SortKey;
        dir: SortDir;
      }>;
      if (typeof s.q === "string") this.filterQuery = s.q;
      if (typeof s.status === "string" && STATUS_LABELS.includes(s.status)) {
        this.filterStatus = s.status;
      }
      if (typeof s.loss === "string") this.filterLossType = s.loss;
      if (typeof s.inspector === "string") this.filterInspector = s.inspector;
      if (typeof s.lossFrom === "string") this.filterLossFrom = s.lossFrom;
      if (typeof s.lossTo === "string") this.filterLossTo = s.lossTo;
      if (typeof s.inspFrom === "string") this.filterInspFrom = s.inspFrom;
      if (typeof s.inspTo === "string") this.filterInspTo = s.inspTo;
      if (s.sort && SORT_OPTIONS.some((o) => o.key === s.sort)) {
        this.sortKey = s.sort;
      }
      if (s.dir === "asc" || s.dir === "desc") this.sortDir = s.dir;
    } catch {
      // Ignore malformed param — fall back to defaults.
    }
  }

  private syncUrl(): void {
    const s: Record<string, string> = {};
    if (this.filterQuery) s.q = this.filterQuery;
    if (this.filterStatus !== "all") s.status = this.filterStatus;
    if (this.filterLossType) s.loss = this.filterLossType;
    if (this.filterInspector) s.inspector = this.filterInspector;
    if (this.filterLossFrom) s.lossFrom = this.filterLossFrom;
    if (this.filterLossTo) s.lossTo = this.filterLossTo;
    if (this.filterInspFrom) s.inspFrom = this.filterInspFrom;
    if (this.filterInspTo) s.inspTo = this.filterInspTo;
    if (this.sortKey !== "updated") s.sort = this.sortKey;
    if (this.sortDir !== "desc") s.dir = this.sortDir;

    const params = new URLSearchParams(window.location.search);
    if (Object.keys(s).length === 0) {
      params.delete(URL_PARAM);
    } else {
      params.set(URL_PARAM, JSON.stringify(s));
    }
    const qs = params.toString();
    const url = window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
    window.history.replaceState(null, "", url);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function labeledField(label: string): HTMLElement {
  const wrap = document.createElement("div");
  wrap.style.cssText = "display:flex;flex-direction:column;gap:5px;flex:1;min-width:180px;";
  const lbl = document.createElement("span");
  lbl.textContent = label;
  lbl.style.cssText = "font-size:10px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:.4px;";
  wrap.appendChild(lbl);
  return wrap;
}

function presetBtnStyle(active: boolean): string {
  return `
    padding: 3px 10px; border-radius: 99px; border: 1px solid;
    font-size: 11px; font-weight: 600; cursor: pointer; transition: all 120ms;
    background: ${active ? "rgba(74,158,255,.2)" : "transparent"};
    border-color: ${active ? "var(--accent)" : "var(--border)"};
    color: ${active ? "var(--accent)" : "var(--text-muted)"};
  `;
}

function refreshPresetStyles(btns: Map<string, HTMLElement>, activeKey: string | null): void {
  for (const [key, btn] of btns) {
    const active = key === activeKey;
    btn.style.background = active ? "rgba(74,158,255,.2)" : "transparent";
    btn.style.borderColor = active ? "var(--accent)" : "var(--border)";
    btn.style.color = active ? "var(--accent)" : "var(--text-muted)";
  }
}

function presetToDates(preset: string): { from: string; to: string } {
  if (!preset) return { from: "", to: "" };
  const now = new Date();
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  const today = toISO(now);
  if (preset === "today") return { from: today, to: today };
  const past = new Date(now);
  past.setDate(past.getDate() - (preset === "7d" ? 7 : 30));
  return { from: toISO(past), to: today };
}

function matchPreset(from: string, to: string): string | null {
  if (!from && !to) return ""; // matches "All" preset
  for (const { key } of DATE_PRESETS) {
    if (!key) continue;
    const r = presetToDates(key);
    if (r.from === from && r.to === to) return key;
  }
  return null;
}

function inDateRange(
  isoDate: string | null | undefined,
  from: string,
  to: string
): boolean {
  if (!from && !to) return true;
  if (!isoDate) return false;
  const d = isoDate.slice(0, 10);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

function formatAddress(addr: IcProject["assignment"]["address"]): string {
  const parts = [addr.line1, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.join(", ");
}

function projectMatchesQuery(p: IcProject, q: string): boolean {
  const a = p.assignment;
  const haystack = [
    a.client_project_id,
    a.inspector,
    a.address.line1,
    a.address.city,
    a.address.state,
    a.address.zip,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function sortProjects(list: IcProject[], key: SortKey, dir: SortDir): IcProject[] {
  const mul = dir === "desc" ? -1 : 1;
  const cmp = (a: IcProject, b: IcProject): number => {
    switch (key) {
      case "updated":
        return cmpStr(a.updated_at, b.updated_at) * mul;
      case "loss_date":
        return cmpNullable(a.assignment.loss_date, b.assignment.loss_date, dir);
      case "last_inspection":
        return cmpNullable(
          a.summary.latest_inspection_date,
          b.summary.latest_inspection_date,
          dir
        );
      case "status":
        return cmpStr(a.assignment.status, b.assignment.status) * mul;
      case "client_id":
        return cmpStr(
          a.assignment.client_project_id || "",
          b.assignment.client_project_id || ""
        ) * mul;
    }
  };
  return list.sort(cmp);
}

function cmpStr(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

// Null-aware compare: nulls always sort to the bottom regardless of direction.
function cmpNullable(a: string | null | undefined, b: string | null | undefined, dir: SortDir): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return cmpStr(a, b) * (dir === "desc" ? -1 : 1);
}

function makeChip(label: string, tone: "ok" | "info" | "muted"): HTMLElement {
  const colors: Record<string, { bg: string; fg: string }> = {
    ok: { bg: "rgba(74,255,106,.15)", fg: "#4aff6a" },
    info: { bg: "rgba(74,158,255,.15)", fg: "#6ab4ff" },
    muted: { bg: "var(--border)", fg: "var(--text-muted)" },
  };
  const c = colors[tone];
  const el = document.createElement("span");
  el.textContent = label;
  el.style.cssText = `
    display:inline-block;padding:2px 8px;border-radius:99px;
    font-size:10px;font-weight:600;
    background:${c.bg};color:${c.fg};
  `;
  return el;
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, (now - t) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
